import { create } from 'zustand';
import type { Folder, Conversation, Document, Message } from '../types';

interface ChatPlusState {
  // Estado
  folders: Folder[];
  conversations: Conversation[];
  documents: Document[];
  messages: Message[];
  
  activeFolderId: string | null;
  activeConversationId: string | null;
  
  isLoading: boolean;
  isSending: boolean;
  
  // Actions
  setFolders: (folders: Folder[]) => void;
  addFolder: (folder: Folder) => void;
  updateFolderInStore: (id: string, name: string) => void;
  removeFolder: (id: string) => void;
  
  setConversations: (conversations: Conversation[]) => void;
  addConversation: (conversation: Conversation) => void;
  removeConversation: (id: string) => void;
  
  setDocuments: (documents: Document[]) => void;
  addDocument: (document: Document) => void;
  removeDocument: (id: string) => void;
  
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  
  setActiveFolderId: (id: string | null) => void;
  setActiveConversationId: (id: string | null) => void;
  
  setIsLoading: (isLoading: boolean) => void;
  setIsSending: (isSending: boolean) => void;
  
  reset: () => void;
}

export const useChatPlusStore = create<ChatPlusState>((set) => ({
  // Estado inicial
  folders: [],
  conversations: [],
  documents: [],
  messages: [],
  
  activeFolderId: null,
  activeConversationId: null,
  
  isLoading: false,
  isSending: false,
  
  // Actions
  setFolders: (folders) => set({ folders }),
  addFolder: (folder) => set((state) => ({ folders: [...state.folders, folder] })),
  updateFolderInStore: (id, name) => set((state) => ({
    folders: state.folders.map(f => f.id === id ? { ...f, name } : f)
  })),
  removeFolder: (id) => set((state) => ({
    folders: state.folders.filter(f => f.id !== id),
    activeFolderId: state.activeFolderId === id ? null : state.activeFolderId,
  })),
  
  setConversations: (conversations) => set({ conversations }),
  addConversation: (conversation) => set((state) => ({
    conversations: [...state.conversations, conversation]
  })),
  removeConversation: (id) => set((state) => ({
    conversations: state.conversations.filter(c => c.id !== id),
    activeConversationId: state.activeConversationId === id ? null : state.activeConversationId,
  })),
  
  setDocuments: (documents) => set({ documents }),
  addDocument: (document) => set((state) => ({ documents: [...state.documents, document] })),
  removeDocument: (id) => set((state) => ({
    documents: state.documents.filter(d => d.id !== id)
  })),
  
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
  
  setActiveFolderId: (id) => set({ activeFolderId: id }),
  setActiveConversationId: (id) => set({ activeConversationId: id }),
  
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsSending: (isSending) => set({ isSending }),
  
  reset: () => set({
    folders: [],
    conversations: [],
    documents: [],
    messages: [],
    activeFolderId: null,
    activeConversationId: null,
    isLoading: false,
    isSending: false,
  }),
}));
